<template>
  <v-dialog
    :value="value"
    :max-width="uploadPhase === 'labeling' ? 860 : 600"
    persistent
    content-class="upload-dialog-scroll"
  >
    <v-card class="upload-dialog-card">
      <v-card-title class="px-6">
        <h5 class="primary-color">
          {{ uploadPhase === 'labeling' ? 'Mark lie ranges' : 'Upload Multiple Videos' }}
        </h5>
      </v-card-title>
      <v-card-text class="mt-4 pb-0 px-6 upload-dialog-body">
        <v-container>
          <template v-if="uploadPhase === 'form'">
            <v-row class="mb-2">
              <v-col cols="12">
                <v-select
                  v-model="selectedAnalysisConfigId"
                  label="Interview Analysis Config *"
                  :items="analysisConfigs"
                  item-text="name"
                  item-value="id"
                  :loading="loadingAnalysisConfigs"
                  dense
                />
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col cols="12">
                <p class="subtitle-2 mb-1">Engines</p>
                <EngineSelector
                  v-model="uploadEngines"
                  source-type="manual_analysis"
                  :disabled="uploading"
                  @change="uploadEngineSelection = $event"
                />
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col cols="12">
                <v-select
                  v-model="folderId"
                  :items="folderItems"
                  item-text="text"
                  item-value="value"
                  label="Folder"
                  dense
                />
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col cols="12">
                <v-text-field
                  v-model="customName"
                  label="Analysis name"
                  dense
                  hint="If uploading one video, if empty it defaults to the uploaded filename. For multiple videos, each one uses its own filename."
                  persistent-hint
                />
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col cols="12">
                <v-textarea v-model="customDescription" label="Description" rows="2" auto-grow dense />
              </v-col>
            </v-row>
            <!-- Drag and drop area -->
            <div
              class="upload-area"
              :class="{ 'drag-over': isDragging }"
              @drop.prevent="onFileDrop"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @click="triggerFileInput"
            >
              <input
                ref="fileInput"
                type="file"
                accept="video/*"
                multiple
                style="display: none"
                @change="onFileSelected"
              />

              <div v-if="selectedFiles.length === 0" class="upload-placeholder">
                <v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
                <h3 class="mt-4">Drag videos here</h3>
                <p class="grey--text">or click to select</p>
                <p class="caption grey--text mt-2">
                  Supported formats: MP4, WebM, AVI, MOV<br />
                  Max size: 10 GB
                </p>
              </div>

              <div v-else class="file-selected">
                <v-icon size="48" color="success">mdi-file-video</v-icon>
                <h4 class="mt-2">{{ selectedFiles.length }} video(s) selected</h4>
                <div class="selected-files-list">
                  <div
                    v-for="(file, index) in selectedFiles"
                    :key="`${getFileKey(file)}-${index}`"
                    class="grey--text caption"
                  >
                    {{ index + 1 }}. {{ file.name }} ({{ formatFileSize(file.size) }})
                  </div>
                </div>
                <v-btn small color="error" outlined class="mt-2" @click.stop="clearFile">
                  <v-icon small left>mdi-close</v-icon>
                  Remove all
                </v-btn>
              </div>
            </div>
          </template>

          <!-- Pre-upload lie labeling: one step per selected video -->
          <template v-else>
            <p class="caption grey--text text--darken-1 mb-1">
              Watch each video and mark the temporal range of the lie (or mark the video as
              truthful) before the upload starts.
            </p>
            <v-stepper v-model="incoherenceStep" vertical non-linear class="incoherence-stepper elevation-0">
              <template v-for="(file, index) in selectedFiles">
                <v-stepper-step
                  :key="`incoherence-step-${getFileKey(file)}`"
                  :step="index + 1"
                  :rules="[() => !isIncoherenceStepInError(index)]"
                  editable
                >
                  {{ file.name }}
                  <small class="mt-1">{{ getIncoherenceStepSummary(index) }}</small>
                </v-stepper-step>
                <v-stepper-content :key="`incoherence-content-${getFileKey(file)}`" :step="index + 1">
                  <IncoherenceRangeStep
                    v-if="incoherenceLabels[index] && incoherenceObjectUrls[index]"
                    :value="incoherenceLabels[index]"
                    :video-url="incoherenceObjectUrls[index]"
                    :active="incoherenceStep === index + 1"
                    @input="onIncoherenceLabelInput(index, $event)"
                  />
                  <div v-if="index < selectedFiles.length - 1" class="d-flex mt-3">
                    <v-spacer />
                    <v-btn
                      small
                      color="primary"
                      :disabled="!isIncoherenceLabelValid(incoherenceLabels[index])"
                      @click="incoherenceStep = index + 2"
                    >
                      Next
                    </v-btn>
                  </div>
                </v-stepper-content>
              </template>
            </v-stepper>
          </template>

          <!-- Upload Progress -->
          <div v-if="uploading" class="mt-4">
            <v-progress-linear :value="uploadProgress" height="25" color="primary" striped>
              <template #default="{ value: progressValue }">
                <strong>{{ Math.ceil(progressValue) }}%</strong>
              </template>
            </v-progress-linear>
            <p class="text-center caption grey--text mt-2">{{ uploadStatus }}</p>
          </div>

          <v-alert
            v-if="messageInside"
            class="mb-0 mt-3"
            outlined
            color="error"
            icon="mdi-alert"
            dismissible
          >
            {{ messageInside }}
          </v-alert>
        </v-container>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn v-if="uploadPhase === 'labeling'" text :disabled="uploading" @click="backToUploadForm">
          Back
        </v-btn>
        <v-btn text class="cancel-modal" :disabled="uploading" @click="cancel">Cancel</v-btn>
        <v-btn
          v-if="uploadPhase === 'form'"
          class="ok-modal"
          :disabled="
            selectedFiles.length === 0 || !selectedAnalysisConfigId || !uploadEngineSelection.valid
          "
          @click="enterIncoherenceLabeling"
        >
          Continue
        </v-btn>
        <v-btn v-else-if="!uploading" class="ok-modal" :disabled="!allIncoherenceLabelsValid" @click="submit">
          Upload
        </v-btn>
        <v-btn v-else class="ok-modal" disabled>
          <v-progress-circular indeterminate color="primary" />
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import EngineSelector from '@/components/analysis/EngineSelector.vue';
import { DEFAULT_ENGINE_CATALOGUE, defaultSelection } from '@/utils/engineRules';
import IncoherenceRangeStep from './IncoherenceRangeStep.vue';

const VALID_TYPES = ['video/mp4', 'video/webm', 'video/avi', 'video/quicktime'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024 * 1024;

/**
 * Upload form of the manual video list (extracted from ManualVideoAnalysisList in M4 F13):
 * configuration, engines, folder, name / description, file picking and the pre-upload lie
 * labelling stepper. The parent performs the uploads and drives `uploading` / `uploadProgress` /
 * `uploadStatus` / `error`; this dialog only validates and emits the request.
 *
 * Emits `input`, `upload` `{ files, incoherenceLabels, selectedAnalysisConfigId, engines, folderId,
 * name, description }` and `cancel`.
 */
export default {
  name: 'UploadVideosDialog',

  components: { EngineSelector, IncoherenceRangeStep },

  props: {
    value: { type: Boolean, default: false },
    analysisConfigs: { type: Array, default: () => [] },
    loadingAnalysisConfigs: { type: Boolean, default: false },
    folders: { type: Array, default: () => [] },
    /** Folder preselected when the dialog opens (null = no folder). */
    defaultFolderId: { type: Number, default: null },
    uploading: { type: Boolean, default: false },
    uploadProgress: { type: Number, default: 0 },
    uploadStatus: { type: String, default: '' },
    /** Error reported by the parent while uploading (shown inside the dialog). */
    error: { type: String, default: '' },
  },

  data() {
    return {
      selectedFiles: [],
      // 'form' = config + file picking, 'labeling' = stepper.
      uploadPhase: 'form',
      incoherenceStep: 1,
      incoherenceLabels: [], // [{ fileKey, noIncoherence, startMs, endMs, durationMs }] parallel to selectedFiles
      incoherenceObjectUrls: [], // local object URLs parallel to selectedFiles (revoked on reset/destroy)
      selectedAnalysisConfigId: null,
      // Engines requested for the uploaded videos (E18); sent by the parent as `runOptions`.
      uploadEngines: [],
      uploadEngineSelection: { engines: [], auto_added: [], valid: false },
      customName: '',
      customDescription: '',
      folderId: null,
      isDragging: false,
      validationMessage: '',
    };
  },

  computed: {
    folderItems() {
      return [
        { text: 'No folder', value: null },
        ...this.folders.map((folder) => ({ text: folder.name, value: folder.id })),
      ];
    },
    messageInside() {
      return this.error || this.validationMessage;
    },
    /** True when every selected file has a valid lie label (or is marked as having no lie). */
    allIncoherenceLabelsValid() {
      return (
        this.incoherenceLabels.length === this.selectedFiles.length &&
        this.incoherenceLabels.every((label) => this.isIncoherenceLabelValid(label))
      );
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
      else this.revokeIncoherenceObjectUrls();
    },
    analysisConfigs(configs) {
      if (!this.selectedAnalysisConfigId && configs?.length) {
        this.selectedAnalysisConfigId = configs[0].id;
      }
    },
  },

  created() {
    if (this.value) this.reset();
  },

  beforeDestroy() {
    this.revokeIncoherenceObjectUrls();
  },

  methods: {
    reset() {
      this.selectedFiles = [];
      this.uploadPhase = 'form';
      this.incoherenceStep = 1;
      this.incoherenceLabels = [];
      this.revokeIncoherenceObjectUrls();
      this.selectedAnalysisConfigId = this.analysisConfigs?.[0]?.id || null;
      // Explicit defaults: EngineSelector only applies its own default on first load.
      this.uploadEngines = defaultSelection(DEFAULT_ENGINE_CATALOGUE, 'manual_analysis');
      this.folderId = this.defaultFolderId;
      this.customName = '';
      this.customDescription = '';
      this.validationMessage = '';
      this.isDragging = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    cancel() {
      this.$emit('cancel');
      this.$emit('input', false);
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const files = Array.from(event.target.files || []);
      if (files.length > 0) this.validateAndSetFiles(files);
      if (event.target) event.target.value = '';
    },
    onFileDrop(event) {
      this.isDragging = false;
      const files = Array.from(event.dataTransfer.files || []);
      if (files.length > 0) this.validateAndSetFiles(files);
    },
    validateAndSetFiles(files) {
      if (files.some((file) => !VALID_TYPES.includes(file.type))) {
        this.validationMessage = 'Unsupported video format. Please use MP4, WebM, AVI or MOV.';
        return;
      }
      if (files.some((file) => file.size > MAX_SIZE_BYTES)) {
        this.validationMessage = 'Video is too large. Maximum 10 GB.';
        return;
      }
      const deduped = [];
      const seen = new Set();
      [...this.selectedFiles, ...files].forEach((file) => {
        const key = this.getFileKey(file);
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(file);
        }
      });
      this.selectedFiles = deduped;
      this.validationMessage = '';
    },
    clearFile() {
      this.selectedFiles = [];
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    getFileKey(file) {
      return `${file.name}-${file.size}-${file.lastModified}`;
    },
    /** A label is valid when the video has no lie, or its range is well-formed. */
    isIncoherenceLabelValid(label) {
      if (!label) return false;
      if (label.noIncoherence) return true;
      const hasValidRange = label.startMs >= 0 && label.endMs > label.startMs;
      // Undecodable preview (e.g. AVI): the range is typed manually, so no duration is known.
      if (label.previewFailed) return hasValidRange;
      return (
        Number.isFinite(Number(label.durationMs)) && Number(label.durationMs) > 0 && hasValidRange
      );
    },
    /** Error styling only once the user has touched the range (avoids flagging pristine steps). */
    isIncoherenceStepInError(index) {
      const label = this.incoherenceLabels[index];
      if (!label || label.noIncoherence) return false;
      const durationMs = Number(label.durationMs);
      const rangeIsEditable = label.previewFailed || (Number.isFinite(durationMs) && durationMs > 0);
      if (!rangeIsEditable) return false;
      if (label.startMs === 0 && label.endMs === 0) return false;
      return label.endMs <= label.startMs;
    },
    getIncoherenceStepSummary(index) {
      const label = this.incoherenceLabels[index];
      if (!label) return '';
      if (label.noIncoherence) return 'Truthful video (no incoherence)';
      if (label.endMs > label.startMs) {
        return `Lie from ${this.formatMsLabel(label.startMs)} to ${this.formatMsLabel(label.endMs)}`;
      }
      return 'Mark the lie range';
    },
    formatMsLabel(ms) {
      const safeSeconds = Math.max(0, Math.round(Number(ms || 0) / 1000));
      const minutes = Math.floor(safeSeconds / 60);
      const seconds = safeSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    onIncoherenceLabelInput(index, label) {
      this.$set(this.incoherenceLabels, index, label);
    },
    enterIncoherenceLabeling() {
      if (this.selectedFiles.length === 0 || !this.selectedAnalysisConfigId) return;
      // Keep labels already filled in for files that are still selected.
      const previousByKey = new Map(this.incoherenceLabels.map((label) => [label.fileKey, label]));
      this.revokeIncoherenceObjectUrls();
      this.incoherenceObjectUrls = this.selectedFiles.map((file) => URL.createObjectURL(file));
      this.incoherenceLabels = this.selectedFiles.map((file) => {
        const fileKey = this.getFileKey(file);
        return (
          previousByKey.get(fileKey) || {
            fileKey,
            noIncoherence: false,
            startMs: 0,
            endMs: 0,
            durationMs: null,
            previewFailed: false,
          }
        );
      });
      this.incoherenceStep = 1;
      this.uploadPhase = 'labeling';
    },
    backToUploadForm() {
      if (this.uploading) return;
      this.uploadPhase = 'form';
    },
    revokeIncoherenceObjectUrls() {
      this.incoherenceObjectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          // Ignore revoke errors on already-released URLs.
        }
      });
      this.incoherenceObjectUrls = [];
    },
    submit() {
      if (this.selectedFiles.length === 0) return;
      if (!this.selectedAnalysisConfigId) {
        this.validationMessage = 'Please select an Interview Analysis Config.';
        return;
      }
      if (!this.allIncoherenceLabelsValid) {
        this.validationMessage = 'Please mark the lie range (or "Truthful video") for every video.';
        return;
      }
      if (!this.uploadEngineSelection.valid) {
        this.validationMessage = 'Please select at least one engine.';
        return;
      }
      this.validationMessage = '';
      this.$emit('upload', {
        files: [...this.selectedFiles],
        incoherenceLabels: this.incoherenceLabels.map((label) => ({ ...label })),
        selectedAnalysisConfigId: this.selectedAnalysisConfigId,
        engines: [...this.uploadEngines],
        folderId: this.folderId,
        name: (this.customName || '').trim(),
        description: (this.customDescription || '').trim(),
      });
    },
    formatFileSize(bytes) {
      if (!bytes || isNaN(bytes)) return '0 Bytes';
      // Base 10 to match Finder and most OS file size displays.
      const k = 1000;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    },
  },
};
</script>

<style scoped>
.upload-area {
  border: 3px dashed #ccc;
  border-radius: 16px;
  padding: 28px 18px;
  min-height: 220px;
  max-height: 360px;
  overflow: hidden;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
}

.upload-area:hover {
  border-color: #6200ea;
  background: #f5f3ff;
}

.upload-area.drag-over {
  border-color: #6200ea;
  background: #e8eaf6;
  transform: scale(1.02);
}

.upload-placeholder h3 {
  color: #333;
  margin: 0;
}

.file-selected {
  padding: 8px 12px;
}

.file-selected h4 {
  color: #333;
  word-break: break-all;
}

.selected-files-list {
  max-height: 140px;
  overflow-y: auto;
  margin-top: 8px;
}

:deep(.upload-dialog-scroll) {
  max-height: 90vh;
}

.upload-dialog-card {
  max-height: 90vh;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.upload-dialog-card .upload-dialog-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.upload-dialog-card .v-card-actions {
  flex: 0 0 auto;
  background: #fff;
}

.incoherence-stepper {
  box-shadow: none !important;
  padding-bottom: 0;
}

.incoherence-stepper :deep(.v-stepper__label) {
  word-break: break-all;
}
</style>
